<template>
  <app-page labelledby="page-title">
    <page-header
      title="Структура организации"
      description="Рабочие места, сотрудники и приглашения в едином списке по иерархии."
    >
      <template #metadata>
        <span v-if="state === 'ready'">
          Занято {{ occupiedCount }} из {{ visibleRoleTotal }} видимых · по тарифу {{ workplaces }} мест
        </span>
      </template>
      <template #actions>
        <q-btn v-if="canEditOrg" color="primary" no-caps icon="add" label="Добавить рабочее место" :disable="!hasCapacity" @click="createRole" />
      </template>
    </page-header>

    <app-state
      v-if="state !== 'ready'"
      :state="state"
      :title="stateTitle"
      :message="stateMessage"
      :action-label="state === 'empty' && canEditOrg ? 'Добавить рабочее место' : ''"
      @action="createRole"
      @retry="requestData"
    />

    <page-section v-else title="Рабочие места" :description="structureDescription">
      <div
        class="organization-tree-scroll"
        role="region"
        aria-label="Дерево рабочих мест от вашего рабочего места"
        tabindex="0"
      >
        <ul
          class="organization-tree"
          role="tree"
          aria-label="Структура рабочих мест"
          data-testid="organization-structure-tree"
        >
          <organization-tree-node
            v-for="role in roles"
            :key="role.key ?? role.unit_info?.id ?? role.label"
            :node="role"
            :can-edit="canEditOrg"
            @select="openRole"
          />
        </ul>
      </div>
      <p v-if="!canEditOrg" class="readonly-note">
        Структура доступна только для просмотра. Для изменения рабочих мест нужны права управления организацией.
      </p>
      <p v-else-if="!hasCapacity" class="readonly-note">
        Все рабочие места тарифа распределены. Добавьте места в тарифном плане, чтобы расширить структуру.
      </p>
    </page-section>
  </app-page>
</template>

<script>
import AppPage from '../../components/ui/AppPage.vue'
import AppState from '../../components/ui/AppState.vue'
import PageHeader from '../../components/ui/PageHeader.vue'
import PageSection from '../../components/ui/PageSection.vue'
import OrganizationTreeNode from '../../components/org/OrganizationTreeNode.vue'

export default {
  name: 'OrganizationStructure',
  components: { AppPage, AppState, OrganizationTreeNode, PageHeader, PageSection },
  data() {
    return {
      state: 'loading', stateTitle: '', stateMessage: '', roles: [], rolesCount: 0,
      visibleRolesCount: 0, workplaces: 0, structureLoaded: false, billingLoaded: false,
      permissionLoaded: false, canEditOrg: false
    }
  },
  computed: {
    flatRoles() {
      const rows = []
      const visit = (node, level = 0) => {
        const unit = node.unit_info || node
        const invite = unit.invite || { empty: true }
        const occupied = !!(unit.email || unit.user)
        const pending = invite.empty === false && invite.is_accepted !== true
        rows.push({
          id: node.key || unit.id,
          level,
          role: unit.role || node.label || 'Рабочее место',
          person: occupied ? (unit.user || unit.email) : (pending ? (invite.display_name || invite.email || 'Приглашение отправлено') : 'Сотрудник не назначен'),
          status: occupied ? 'Занято' : (pending ? 'Приглашение' : 'Вакантно'),
          tone: occupied ? 'success' : (pending ? 'warning' : 'neutral'),
          icon: occupied ? 'person' : (pending ? 'mail_outline' : 'person_outline')
        })
        ;(node.children || []).forEach((child) => visit(child, level + 1))
      }
      this.roles.forEach((role) => visit(role))
      return rows
    },
    occupiedCount() { return this.flatRoles.filter((item) => item.status === 'Занято').length },
    visibleRoleTotal() { return this.visibleRolesCount || this.flatRoles.length },
    hasCapacity() { return this.workplaces > this.rolesCount },
    structureDescription() {
      const visible = this.visibleRoleTotal
      const visibleLabel = `${visible} ${this.pluralize(visible, ['рабочее место', 'рабочих места', 'рабочих мест'])} от вашего места`
      return visible === this.rolesCount ? visibleLabel : `${visibleLabel} · всего в организации ${this.rolesCount}`
    }
  },
  created() {
    this.onStructure = (response) => {
      if (response.error) { this.showError('Не удалось загрузить структуру'); return }
      const root = response.roles
      this.roles = Array.isArray(root) ? root.filter(Boolean) : (root && typeof root === 'object' ? [root] : [])
      this.rolesCount = Number(response.roles_count ?? this.flatRoles.length)
      this.visibleRolesCount = Number(response.visible_roles_count ?? this.flatRoles.length)
      this.structureLoaded = true; this.finishLoading()
    }
    this.onBilling = (response) => {
      if (!response.error && response.data) {
        this.workplaces = (response.data.modules || []).filter((module) => module.is_active)
          .reduce((total, module) => total + Number(module.template?.workplaces || 0) + Number(module.workplaces || 0), 0)
      }
      this.billingLoaded = true; this.finishLoading()
    }
    this.onUser = (response) => {
      this.canEditOrg = response.user?.role?.permissions?.edit_org === true
      this.permissionLoaded = true; this.finishLoading()
    }
    this.emitter.on('didReceiveOrgStructure', this.onStructure)
    this.emitter.on('onBillingDetails', this.onBilling)
    this.emitter.on('didReceoveUserInfoAcrossOrg', this.onUser)
    if (this.connection.isAuthenticated) this.requestData()
    else this.emitter.on('connectionReady', this.requestData)
  },
  beforeUnmount() {
    this.emitter.off('didReceiveOrgStructure', this.onStructure)
    this.emitter.off('onBillingDetails', this.onBilling)
    this.emitter.off('didReceoveUserInfoAcrossOrg', this.onUser)
    this.emitter.off('connectionReady', this.requestData)
  },
  methods: {
    requestData() {
      this.state = 'loading'; this.structureLoaded = false; this.billingLoaded = false; this.permissionLoaded = false
      this.connection.getOrgStructure(this.connection.makeRequestId('structure'))
      this.connection.getBillingDetails(this.connection.makeRequestId('structure-billing'))
      this.connection.getUserInfoAcrossOrg(this.connection.makeRequestId('structure-permission'))
    },
    finishLoading() {
      if (!this.structureLoaded || !this.billingLoaded || !this.permissionLoaded || this.state === 'error') return
      this.state = this.roles.length ? 'ready' : 'empty'
      if (!this.roles.length) { this.stateTitle = 'Структура пока пуста'; this.stateMessage = this.canEditOrg ? 'Добавьте первое рабочее место.' : 'Обратитесь к администратору организации.' }
    },
    showError(title) { this.state = 'error'; this.stateTitle = title; this.stateMessage = 'Проверьте подключение и повторите запрос.' },
    createRole() {
      if (!this.canEditOrg) return
      if (!this.hasCapacity) { this.$q.notify({ color: 'warning', message: 'В тарифе нет свободных рабочих мест' }); return }
      this.$router.push({ name: 'org_role_new' })
    },
    openRole(id) { if (this.canEditOrg) this.$router.push({ name: 'org_role_settings', params: { id } }) },
    pluralize(value, forms) {
      const abs = Math.abs(value) % 100; const last = abs % 10
      if (abs > 10 && abs < 20) return forms[2]
      if (last > 1 && last < 5) return forms[1]
      if (last === 1) return forms[0]
      return forms[2]
    }
  }
}
</script>

<style scoped>
.organization-tree-scroll {
  max-width: 100%;
  overflow-x: auto;
  padding: var(--sid-space-1) var(--sid-space-1) var(--sid-space-4);
}
.organization-tree-scroll:focus-visible { outline: 2px solid var(--sid-color-brand-600); outline-offset: 2px; }
.organization-tree { display: grid; gap: var(--sid-space-3); min-width: min-content; margin: 0; padding: 0; }
.readonly-note { margin: var(--sid-space-4) 0 0; color: var(--sid-color-text-secondary); }
</style>
